import { AfterViewChecked, Component, Input, OnDestroy, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { isEmpty, cloneDeep } from 'lodash';
import { forkJoin, tap } from 'rxjs';
import { HisAuthService } from '../../auth/service/his-auth.service';
import { GenericValue } from '../../core/model/generic-value';
import { HisConfirmationService } from '../../core/service/base/his-confirmation.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { CommunicationLogUiText } from '../communicationlog-uitexts';
import { CommunicationLogFilterSettings } from '../model/communiacation-log-filter-settings';
import { ConversationRecordEditData } from '../model/communication-log-edit-data.model';
import { CommunicationLogEntry, } from '../model/communication-log-entry.model';
import { HisCommunicationlogService, byTimeNewestFirst } from '../service/communicationlog.service';
import { CommunicationLogEntriesComponent } from './communication-log-entries/communication-log-entries.component';
import { ConversationRecordEditComponent } from './conversation-record-edit/conversation-record-edit.component';
import { CommunicationLogFilter } from '../model/communication-log-filter';

export class CommunicationLogFilterValue {
    constructor( public key: string, public value: boolean ) { }
}

type DatePredicate = ( date: Date ) => boolean;

@Component( {
    selector: 'his-communicationlog',
    templateUrl: './communicationlog.component.html',
    styleUrl: './communicationlog.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisCommunicationlogComponent implements OnInit, AfterViewChecked, OnDestroy {
    private communicationlogService = inject(HisCommunicationlogService);
    private confirmationService = inject(HisConfirmationService);
    private notificationService = inject(HisNotificationService);
    private hisUiService = inject(HisUiService);
    protected auth = inject(HisAuthService);

    @Input( { required: true } ) ownerId: number;
    @Input() domainRestriction: number[];
    @Input() renderTitle: boolean = true;
    @Input() renderDialogHeader: boolean = true;
	@Input() authorizationContext: string;
	@Input() infoText: string;

    @ViewChild( 'creationDialog' ) private creationDialog: ConversationRecordEditComponent;
    @ViewChild( 'logEntries' ) private logEntriesComponent: CommunicationLogEntriesComponent;

    protected logEntries: CommunicationLogEntry[] = null;
    protected filteredLogEntries: CommunicationLogEntry[] = null;
    protected allDomains: GenericValue[] = [];
    protected availableDomains: GenericValue[] = [];
    protected uiText = CommunicationLogUiText;
    protected renderSelf: boolean = false;
    protected allEntriesLoaded = false;
    protected filters: CommunicationLogFilterValue[] = [];
    protected canCreateConversationRecord: boolean;

    private filterSettings: CommunicationLogFilterSettings = null;
    private initialized: boolean = false;
    private pageSize: number;
    private filterDefinitions: CommunicationLogFilter[] = [];

    constructor() {
        this.canCreateConversationRecord = this.auth.hasAny( 'RIGHT_CS_SYS_APPLOG_CREATE_COMMUNICATION_NOTES' );
    }

    ngOnInit(): void {
        this.initData();
    }

    ngAfterViewChecked(): void {
        if ( !this.initialized && this.logEntriesComponent ) {
            this.logEntriesComponent.scrollToLatestEntry();
            this.initialized = true;
        }
    }

    ngOnDestroy(): void {
        this.notificationService.clear();
    }

    initData(): void {
        const allDomains$ = this.communicationlogService.getAllDomains( this.ownerId ).pipe( tap( domains => {
            this.allDomains = domains;
        } ) );

        const logEntires$ = this.communicationlogService.findByOwnerId( this.ownerId, this.authorizationContext ).pipe(
            tap( entries => {
                this.logEntries = entries;
                this.filteredLogEntries = entries;
                this.checkIfAllEntriesAreLoaded( entries );
            } ) );

        this.communicationlogService.getPageSize().subscribe( pageSize => this.pageSize = pageSize );
        this.communicationlogService.getFilterDefinitions().subscribe( ( filterDefinitions: CommunicationLogFilter[] ) => {
            this.filterDefinitions = filterDefinitions;
            this.filters = filterDefinitions.map( definition => new CommunicationLogFilterValue( definition.key, true ) );
        } );

        forkJoin( [allDomains$, logEntires$] ).subscribe( () => {
            this.initAvailableDomains();
            this.initDomainRestriction();
            this.applyFilterSettings( this.filterSettings );
            this.initialized = false;
            this.renderSelf = true;
        } );
    }

    private checkIfAllEntriesAreLoaded( entries: CommunicationLogEntry[] ) {
        if ( entries.length < this.pageSize ) {
            this.allEntriesLoaded = true;
        }
    }

    private initDomainRestriction(): void {
        if ( !isEmpty( this.domainRestriction ) && !this.filterSettings ) {
            this.filterSettings ??= new CommunicationLogFilterSettings();
            this.filterSettings.domains = [...new Set( this.domainRestriction )];
            this.applyFilterSettings( this.filterSettings );
        }
    }

    protected applyFilterSettings( filterSettings: CommunicationLogFilterSettings ): void {
        this.filterSettings = filterSettings;
        const filteredEntries = [...this.logEntries].filter( entry => {
            return this.filterByDateRange( entry )
                && this.filterBySender( entry )
                && this.filterByType( entry )
                && this.fitlerByDomain( entry );
        } );

        this.filteredLogEntries = filteredEntries;
    }

    private truncatedCreationDateMeetsPredicate( entry: CommunicationLogEntry, predicate: DatePredicate ): boolean {
        const creationDate = new Date( entry.createdAt );
        creationDate.setHours( 0, 0, 0, 0 );
        return predicate( creationDate );
    }

    private filterByDateRange( entry: CommunicationLogEntry ): boolean {
        if ( !this.filterSettings ) {
            return true;
        }

        if ( this.filterSettings.from && this.filterSettings.to ) {
            return this.truncatedCreationDateMeetsPredicate( entry, date => {
                return date >= this.filterSettings.from && date <= this.filterSettings.to;
            } );
        }

        if ( this.filterSettings.from ) {
            return this.truncatedCreationDateMeetsPredicate( entry, date => {
                return date >= this.filterSettings.from;
            } );
        }

        if ( this.filterSettings.to ) {
            return this.truncatedCreationDateMeetsPredicate( entry, date => {
                return date <= this.filterSettings.to;
            } );
        }

        return true;
    }

    private filterBySender( entry: CommunicationLogEntry ): boolean {
        if ( !this.filterSettings ) {
            return true;
        }
        const filterByOwnerActivity = this.ownerActivityFilterActive && this.filterSettings.ownerActivity;

        if ( !this.filterSettings.ownActivity && !this.filterSettings.universityActivity && !filterByOwnerActivity ) {
            return true;
        }

        if ( this.filterSettings.ownActivity && entry.senderId === this.auth.personId ) {
            return true;
        }

        if ( filterByOwnerActivity && entry.senderId === this.ownerId ) {
            return true;
        }

        if ( this.filterSettings.universityActivity && entry.senderId !== this.ownerId ) {
            return true;
        }

        return false;
    }

    private filterByType( entry: CommunicationLogEntry ): boolean {
        if ( this.filterDefinitions && this.filterDefinitions.length > 0 ) {
            for ( let definition of this.filterDefinitions ) {
                if ( this.isFilterActive( definition.key ) && this.filterDefinitionMatchesEntry( definition, entry ) ) {
                    return true;
                }
            }
        }
        return false;
    }

    private fitlerByDomain( entry: CommunicationLogEntry ): boolean {
        if ( !this.filterSettings ) {
            return true;
        }

        if ( isEmpty( this.filterSettings.domains ) ) {
            return true;
        }

        return entry.domainIds.some( domainId => {
            return this.filterSettings.domains.includes( domainId );
        } );
    }

    private filterDefinitionMatchesEntry( definition: CommunicationLogFilter, entry: CommunicationLogEntry ): boolean {
        const isType = this.matches( entry.type, definition.config.type );
        const isSubtype = this.matches( entry.subtype, definition.config.subtype );

        if ( isType && isSubtype ) {
            return true;
        }
        return false;
    }

    private matches( valueToMatch: string, pattern: string ): boolean {
        if ( isEmpty( pattern ) ) {
            return true;
        }

        return !!valueToMatch.match( pattern );
    }

    private isFilterActive( key: string ): boolean {
        // Note: this.filterSettings may be null
        if ( this.filterSettings ) {
            return this.filterSettings.filters.includes( key );
        }
        return false;
    }

    private initAvailableDomains(): void {
        const usedDomainIds = [...new Set( this.logEntries.flatMap( entry => entry.domainIds ) )];
        this.availableDomains = this.allDomains.filter( domain => usedDomainIds.includes( domain.id ) );
    }

    protected saveConversationRecord( logEntry: ConversationRecordEditData ): void {
        if ( !logEntry.id ) {
            this.createNewConversationRecord( logEntry );
        }
        else {
            this.updateConversationRecord( logEntry );
        }
    }

    private updateConversationRecord( logEntry: ConversationRecordEditData ) {
        this.communicationlogService.updateConversationRecord( logEntry ).subscribe( () => {
            this.creationDialog.hideDialog();
            const oldEntry = this.logEntries.find( entry => entry.id === logEntry.id );
            const editedEntry = cloneDeep( oldEntry );
            editedEntry.title = logEntry.title;
            editedEntry.domainIds = logEntry.domainIds;
            editedEntry.additionalAttributes.text = logEntry.text;
            const index = this.logEntries.findIndex( entry => entry.id === logEntry.id );
            this.logEntries.splice( index, 1, editedEntry );
            this.applyFilterSettings( this.filterSettings );
            this.notificationService.successI18n( this.uiText.SUCCESS_SAVE );
        } );
    }

    private createNewConversationRecord( newLogEntry: ConversationRecordEditData ): void {
        this.communicationlogService.createConversationRecord( this.ownerId, newLogEntry, this.authorizationContext ).subscribe( () => {
            this.notificationService.clear();
            this.communicationlogService.findByOwnerId( this.ownerId, this.authorizationContext ).pipe( tap( this.checkIfAllEntriesAreLoaded.bind( this ) ) )
                .subscribe( entries => {
                    //entries may contain data, that has been updated -> replace "old" data
                    const notUpdatedEntries = this.logEntries.filter( entry => !entries.some( other => other.id === entry.id ) );
                    //merge and sort by creation date
                    this.logEntries = [...entries, ...notUpdatedEntries].sort( byTimeNewestFirst );
                    this.applyFilterSettings( this.filterSettings );
                    this.creationDialog.hideDialog();
                    this.notificationService.successI18n( this.uiText.SUCEESS_SAVE_NEW );
                } );
        } );
    }

    protected deleteEntryById( id: number ): void {
        const entryToDelete = this.logEntries.find( entry => entry.id === id );
        this.confirmationService.confirm( {
            header: this.hisUiService.translate( this.uiText.DELETE_CONFIRMATION_HEADER ),
            message: this.hisUiService.translate( this.uiText.DELETE_RECORD_QUESTION, entryToDelete.title ),
            accept: () => {
                this.communicationlogService.deleteEntry( id ).subscribe( () => {
                    this.logEntries = this.logEntries.filter( entry => entry.id !== id );
                    this.applyFilterSettings( this.filterSettings );
                    this.notificationService.deleteSuccessI18n();
                } );
            }
        } );
    }

    protected editEntryById( id: number ): void {
        const entryToEdit = this.logEntries.find( entry => entry.id === id );

        const data = new ConversationRecordEditData();
        data.id = id;
        data.title = entryToEdit.title;
        data.text = entryToEdit.additionalAttributes?.text;
        data.domainIds = [...entryToEdit.domainIds];
        this.creationDialog.editEntry( data );
    }

    protected loggedInPersonId(): number {
        return this.auth.personId;
    }

    protected getPersonName( id: number ): string {
        return this.communicationlogService.getParticipantName( id )?.displayname;
    }

    protected shouldRenderDialogHeader(): boolean {
        if ( !this.renderDialogHeader ) {
            return false;
        }

        return this.auth.personId !== this.ownerId;
    }

    protected loadMoreEntries(): void {
        if ( !this.canLoadMoreEntries() ) {
            return;
        }

        const lastEntry = this.logEntries[this.logEntries.length - 1];
        this.communicationlogService.findByOwnerId( this.ownerId, this.authorizationContext, lastEntry.createdAt ).subscribe( entries => {
            const newEntries = entries.filter( entry => !this.logEntries.some( existingEntry => existingEntry.id === entry.id ) );
            this.logEntries = [...this.logEntries, ...newEntries];
            this.checkIfAllEntriesAreLoaded( entries );
            if ( this.canLoadMoreEntries() && !this.hasScrollbar() ) {
                this.loadMoreEntries();
            }
            this.applyFilterSettings( this.filterSettings );
        } );

    }

    protected canLoadMoreEntries(): boolean {
        return !this.allEntriesLoaded;
    }

    private hasScrollbar(): boolean {
        return document.documentElement.scrollHeight - document.documentElement.clientHeight > 0;

    }

    protected get ownerActivityFilterActive(): boolean {
        return this.auth.personId !== this.ownerId;
    }
}
